<template>
  <div class="m-4 md:mx-8 md:mt-16">
    <section>
      <h1 class="px-4 md:px-8 py-2 text-2xl font-bold bg-primario text-gray-200 tracking-wide rounded-t">
        {{ title }} ({{ totalRows }})
      </h1>
      <div class="bg-white p-4 md:p-8 rounded-b-sm">
        <form class="flex flex-wrap md:flex-no-wrap justify-between md:space-x-3 space-y-3 md:space-y-0">
          <div class="flex w-full flex-col space-y-3">
            <div class="relative inline-block text-gray-500 focus-within:text-gray-700 md:max-w-xs">
              <span class="absolute inset-y-0 right-0 outline-none">
                <svg class="w-5 h-5 mr-2 mt-1.5 fill-current outline-none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.32 14.9l5.39 5.4a1 1 0 0 1-1.42 1.4l-5.38-5.38a8 8 0 1 1 1.41-1.41zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z" />
                </svg>
              </span>
              <input
                v-model="filters.searchReference"
                type="text"
                :placeholder="$t('lang.msg.searchByReference')"
                class="form-input w-full h-8 pr-8 appearance-none border border-gray-500 rounded py-2 pl-4 text-gray-900 placeholder-gray-500 leading-5 focus:outline-none focus:shadow-outline"
              >
            </div>
            <div class="flex items-center space-x-2">
              <div v-if="hasRoles(['CLIENTE'])" class="w-full md:max-w-xs" />
              <Select
                v-if="!hasRoles(['CLIENTE'])"
                id="idClient"
                :data="clients"
                class="md:max-w-xs"
                track-by="clientName"
                :placeholder="$t('lang.msg.searchByClient')"
                v-model.number="filters.idClient"
                @input="changeFilter"
              />
              <div class="flex items-center">
                <input
                  type="checkbox"
                  class="form-checkbox bg-gray-200 text-blue-700 cursor-pointer"
                  v-model="filters.fabricaAsignada"
                  @input="changeFilter"
                >
                <span class="text-sm text-gray-600 ml-2 -mt-0.25">
                  {{ $t('lang.msg.factoryAssigned') }}
                </span>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <Select
                id="idCollection"
                :data="collections"
                class="md:max-w-xs"
                :track-by="getLangLabel('collection')"
                :placeholder="$t('lang.msg.searchByCollection')"
                v-model.number="filters.idCollection"
                @input="changeFilter"
              />
              <div class="flex items-center">
                <input
                  type="checkbox"
                  class="form-checkbox bg-gray-200 text-blue-700 cursor-pointer"
                  v-model="filters.agenteAsignado"
                  @input="changeFilter"
                >
                <span class="text-sm text-gray-600 ml-2 -mt-0.25">{{ $t('lang.msg.agentAssigned') }}</span>
              </div>
            </div>
          </div>
          <div class="flex flex-col justify-end w-full md:max-w-72 space-y-3">
            <ButtonRedirect route="planningDesign" v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])">
              {{ $t("lang.msg.addSample") }}
            </ButtonRedirect>
            <!-- === FILTROS === -->
            <div class="flex space-x-2">
              <ButtonAction large color="verde" @action="filterAction">
                {{ $t("lang.msg.filter") }}
              </ButtonAction>
              <ButtonAction large color="red" @action="removeFilters(`api/Samples/PaginationNoProduction`, `api/Samples/TotalRegistrosNoProduction`)">
                {{ $t("lang.msg.removeFilters") }}
              </ButtonAction>
            </div>
            <!-- ================================ -->
            <!-- === ACTIVOS / DESCATALOGADOS === -->
            <!-- ================================ -->
            <ButtonAction
              v-if="showingActivePlannings"
              v-show="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
              large
              color="lightgray"
              @action="getDiscontinuedPlannings(`api/Samples/PaginationDescatalogada`, `api/Samples/TotalRegistrosDescatalogada`)"
            >
              {{ $t("lang.msg.viewDiscontinued") }}
            </ButtonAction>
            <ButtonAction
              v-else
              large
              color="lightgray"
              @action="removeFilters(`api/Samples/Pagination`, `api/Samples/TotalRegistros`)"
            >
              {{ $t("lang.msg.viewActivePlannings") }}
            </ButtonAction>
          </div>
        </form>

        <PlanningPagination
          :current-page="page"
          :total-rows="totalRows"
          :visible-rows="visibleRows"
          @next-page="fetchPaginationData('next');"
          @previous-page="fetchPaginationData('previous');"
          @load-selected-page="fetchPaginationData"
          @take-rows="takeRows"
        />

        <!-- === PLANNING TABLE === -->
        <ul id="PlanningTable">
            <draggable v-model="planning"  :disabled="isSmallScreen">

            <li
            v-for="(item, index) in planning"
            :key="`item_${index}`"
            class="mt-6 pr-2 grid items-center grid-cols-1 sm:grid-cols-2 xl:grid-cols-design gap-0 sm:gap-4"
            :class="{'bg-gray-200':index % 2 == 0,'border-red':item.descatalogada}"
          >
              <PlanningImages
                  :id-comercial-request="item.idComercialRequest"
                  :join-life="item.joinLife"
                  :reoperate="item.productionReoperation"
                  :official-delivery-date1="item.officialDeliveryDate1"
                  :reference-number="item.comercialRequestReferenceNumber"
                  :discontinued="item.descatalogada"
                  :cloned="item.cloned"
              />
            <div class="my-2 cursor-pointer" @click="viewPlanning(index)">
              <div class="xl:space-y-2">
                <PlanningTableItem :index="index" :data="item.clientName" :label="$t('lang.msg.client')" />
                <PlanningTableItem :index="index" :data="item.factoryName" :label="$t('lang.msg.factory')" />
                <PlanningTableItem :index="index" :data="item.factoryLocation" :label="$t('lang.msg.location')" />
                <PlanningTableItem :index="index" :data="item.fechaEstimadaLlegada|moment" :label="$t('lang.msg.estimatedDate')" />
              </div>
            </div>
            <div class="my-2 cursor-pointer" @click="viewPlanning(index)">
              <div class="xl:space-y-2">
                <PlanningTableItem
                  :index="index"
                  :data="concatTwoValues(
                    item.comercialRequestReference,
                    item.comercialRequestReferenceNumber
                  )"
                  :label="$t('lang.msg.reference')"
                />
                <PlanningTableItem :index="index" :data="item.sectionName" :label="$t('lang.msg.department')" />
                <PlanningTableItem :index="index" :data="getLangValue(item, 'collection')" :label="$t('lang.msg.collection')" />
                <PlanningTableItem :index="index" :data="getLangValue(item, 'weight')" :label="$t('lang.msg.weight')" />
              </div>
            </div>
            <div class="my-2 cursor-pointer" @click="viewPlanning(index)">
              <div class="xl:space-y-2">
                <PlanningTableItem :index="index" :data="getLangValue(item, 'baseColor')" :label="$t('lang.msg.baseColor')" />
                <PlanningTableItem :index="index" :data="item.size" :label="$t('lang.msg.size')" />
                <PlanningTableItem :index="index" :data="getLangValue(item, 'composition')" :label="$t('lang.msg.composition')" />
                <PlanningTableItem :index="index" :data="getLangValue(item, 'quality')" :label="$t('lang.msg.quality')" />
              </div>
            </div>
            <PlanningDays
              :index="index"
              class="cursor-pointer"
              :delivery-date="item.fechaEstimadaLlegada"
              :activation-date="item.sampleDateSignUp"
              :end-date="item.sampleToProductionDate"
              :is-finished="item.sampleToProduction"
              :isDelivered="item.sampleDelivered"
              @click="viewPlanning(index)"
            />
            <div class="xl:my-2">
              <div class="flex justify-end items-center xl:flex-col space-x-4 xl:space-x-0 xl:space-y-3">
                  <ButtonRounded
                      title="Mover"
                      color=""
                      large
                      v-if="!isSmallScreen"
                  >
                      <svg class="w-6 h-6 fill-current" viewBox="0 0 28 28" stroke="currentColor">
                          <path d="M28 14c0 0.266-0.109 0.516-0.297 0.703l-4 4c-0.187 0.187-0.438 0.297-0.703 0.297-0.547 0-1-0.453-1-1v-2h-6v6h2c0.547 0 1 0.453 1 1 0 0.266-0.109 0.516-0.297 0.703l-4 4c-0.187 0.187-0.438 0.297-0.703 0.297s-0.516-0.109-0.703-0.297l-4-4c-0.187-0.187-0.297-0.438-0.297-0.703 0-0.547 0.453-1 1-1h2v-6h-6v2c0 0.547-0.453 1-1 1-0.266 0-0.516-0.109-0.703-0.297l-4-4c-0.187-0.187-0.297-0.438-0.297-0.703s0.109-0.516 0.297-0.703l4-4c0.187-0.187 0.438-0.297 0.703-0.297 0.547 0 1 0.453 1 1v2h6v-6h-2c-0.547 0-1-0.453-1-1 0-0.266 0.109-0.516 0.297-0.703l4-4c0.187-0.187 0.438-0.297 0.703-0.297s0.516 0.109 0.703 0.297l4 4c0.187 0.187 0.297 0.438 0.297 0.703 0 0.547-0.453 1-1 1h-2v6h6v-2c0-0.547 0.453-1 1-1 0.266 0 0.516 0.109 0.703 0.297l4 4c0.187 0.187 0.297 0.438 0.297 0.703z"></path>
                      </svg>
                  </ButtonRounded>
                <ButtonRounded
                  @action="openNotificationsModal(item)"
                  title="Ver notas ext."
                  color="blue"
                  large
                >
                  <svg class="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 10H8.01M12 10H12.01M16 10H16.01M9 16H5C3.89543 16 3 15.1046 3 14V6C3 4.89543 3.89543 4 5 4H19C20.1046 4 21 4.89543 21 6V14C21 15.1046 20.1046 16 19 16H14L9 21V16Z" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </ButtonRounded>
                <ButtonRounded
                  v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
                  @action="confirmStepToProduction(index)"
                  title="Enviar a producción"
                  color="blue"
                  large
                >
                  <svg class="w-6 h-6 fill-current" viewBox="0 0 64 64" stroke="currentColor">
                    <path d="M58.88 36.65c0-.18.18-.35.35-.53.35-.18.71-.35 1.06-.53.35-.18.53-.35.71-.89-.18-.89-.35-1.77-.53-2.48-.18-.35-.35-.53-.89-.53h-1.24c-.18 0-.53-.18-.53-.35-.35-.35-.53-.71-.71-1.06-.18-.18-.18-.53 0-.71.18-.35.35-.71.53-1.06.18-.35.18-.71-.18-.89-.71-.53-1.42-.89-2.12-1.42-.35-.18-.53 0-.89.18-.18.35-.53.53-.71.71-.18.35-.53.35-.89.35-.35 0-.71-.18-1.24-.18-.18 0-.53-.18-.53-.53-.18-.35-.18-.71-.35-1.06 0-.18-.18-.35-.35-.53-.18 0-.35-.18-.53 0-.89.18-1.77.35-2.48.53-.53.18-.53.35-.53.89v1.24c0 .18-.18.53-.35.53-.35.35-.71.53-1.06.71-.35.18-.53.18-.89 0-.35-.18-.71-.35-1.06-.53-.35-.18-.53-.18-.89.18-.53.71-1.06 1.59-1.42 2.3-.16.35-.16.52.19.88.35.18.53.53.89.71.35.18.35.53.35.89-.18.53-.18.89-.18 1.24s-.35.53-.53.53c-.35.18-.71.35-1.06.35-.35.18-.53.35-.53.89.18.71.35 1.59.53 2.48.18.53.35.53.89.53h1.06c.18 0 .53.18.53.35.35.35.53.71.71 1.24.18.18.18.53 0 .71-.18.35-.35.71-.53 1.06-.18.35-.18.53.18.71.71.53 1.59 1.06 2.3 1.59.35.18.53.18.89-.18.35-.35.53-.53.71-.89.18-.35.53-.35.89-.35.53 0 .89.18 1.24.18.18 0 .53.18.53.53.18.35.35.71.53 1.06.18.35.35.53.71.53.89-.18 1.59-.35 2.48-.53.53-.18.53-.35.53-.89v-1.06c0-.18.18-.53.35-.71.35-.35.71-.53 1.06-.71.18-.18.53-.18.71 0 .35.18.71.35 1.06.53.35.18.53.18.89-.18.53-.71 1.06-1.59 1.59-2.3.18-.35 0-.53-.18-.89-.35-.18-.53-.53-.89-.71-.35-.18-.35-.53-.35-.71-.01-.34-.01-.69.17-1.22zm-4.25 1.59c-1.24 2.3-4.25 3.19-6.55 1.77-2.3-1.24-3.19-4.25-1.77-6.55 1.24-2.3 4.25-3.19 6.55-1.77 2.12 1.42 3.01 4.07 1.77 6.55z" />
                    <path d="M36.93 49.39c.35-.35.71-.53 1.06-.53.71 0 1.59 0 2.3.18.71 0 1.42-.35 1.59-1.06.35-1.59.71-3.19.89-4.78.18-.71-.18-1.24-1.06-1.42-.71-.18-1.42-.53-2.12-.71-.35-.18-.71-.53-.89-.89-.35-.89-.53-1.77-.53-2.66 0-.53.18-1.06.53-1.42.53-.53 1.06-1.06 1.59-1.42.71-.53.89-1.06.53-1.77-.89-1.42-1.77-2.83-2.83-4.25-.35-.53-1.06-.71-1.77-.35-.53.35-1.24.53-1.77.89-.71.35-1.24.35-1.77-.18s-1.42-.89-1.95-1.42c-.35-.35-.53-.71-.53-1.24 0-.71.18-1.42.18-2.12 0-.35 0-.71-.18-1.06-.35-.18-.53-.53-.89-.53-1.59-.35-3.19-.71-4.78-.89-.89-.18-1.24.18-1.59 1.06-.35.71-.53 1.42-.71 2.12-.18.35-.53.71-1.06.89-.71.18-1.59.35-2.48.53-.53.18-1.06 0-1.59-.53s-.89-1.06-1.42-1.59c-.53-.53-1.06-.71-1.59-.35-1.42.89-3.01 1.77-4.25 2.83-.53.35-.53.89-.35 1.59.35.53.53 1.24.89 1.95.35.53.35 1.24 0 1.77-.53.71-.89 1.24-1.24 1.95-.35.53-.71.71-1.42.71s-1.42 0-2.12-.18c-.89 0-1.42.18-1.59 1.06-.35 1.59-.71 3.01-.89 4.6-.18.89.18 1.42 1.06 1.59.71.18 1.42.35 2.12.71.35.18.71.53.89.89.35.89.53 1.77.53 2.66 0 .53-.18.89-.53 1.24-.53.53-1.24 1.06-1.77 1.59s-.71.89-.35 1.59c.89 1.42 1.95 3.01 3.01 4.43.35.53.89.53 1.59.35.53-.35 1.24-.53 1.95-.89.53-.35 1.24-.35 1.77 0 .71.53 1.42.89 2.12 1.42.35.35.53.71.53 1.24 0 .71 0 1.59-.18 2.3 0 .71.35 1.24 1.06 1.42 1.59.35 3.19.71 4.78.89.89.18 1.24-.18 1.59-1.06.18-.71.35-1.42.71-2.12.18-.35.53-.89 1.06-.89.89-.35 1.77-.53 2.66-.53.53 0 1.06.18 1.42.53.53.53.89 1.06 1.42 1.59s1.06.71 1.59.53c1.42-.89 3.01-1.95 4.43-3.01.53-.35.53-1.06.35-1.59-.35-.53-.53-1.24-.89-1.95-.35-.53-.35-1.06 0-1.77 0-.52.36-1.23.89-1.94zm-8.32-.53c-4.07 3.19-9.74 2.3-12.74-1.77s-2.3-9.74 1.77-12.74c4.07-3.01 9.91-2.3 12.92 1.95 2.47 3.71 2.12 9.38-1.95 12.56z" />
                    <path d="M47.19 5.85c.18-.53.18-.89-.35-1.24-.89-.53-1.95-1.06-3.01-1.59-.53-.18-.89 0-1.24.35s-.53.71-.71 1.06c-.35.53-.53.53-1.06.53s-1.06 0-1.59-.18c-.35 0-.53-.35-.71-.53-.35-.53-.53-.89-.71-1.42l-.53-.53c-.35 0-.53-.18-.71 0-1.06.35-2.12.53-3.19.89-.53.18-.71.53-.53 1.06 0 .53.18 1.06.18 1.59 0 .35-.18.53-.35.89-.35.35-.89.71-1.24 1.06-.35.18-.71.35-1.06.18-.53-.18-.89-.35-1.42-.53-.35-.18-.89-.18-1.06.35-.53 1.06-1.06 1.95-1.59 3.01-.18.35 0 .71.35 1.06.35.35.71.53 1.24.89.35.35.53.53.53 1.06s0 1.06-.18 1.59c0 .53-.18.71-.53.89-.53.18-.89.35-1.24.53-.53.35-.71.53-.53 1.24.35 1.06.53 2.12.89 3.01.18.53.53.71 1.06.53.53 0 .89-.18 1.42-.18.35 0 .71.18.89.35.53.35.89.89 1.24 1.42.18.18.18.53.18.89-.18.53-.35 1.06-.53 1.59-.18.35-.18.71.35 1.06 1.06.53 2.12 1.24 3.19 1.77.53.18.71 0 1.06-.35.35-.35.53-.71.89-1.24.35-.35.53-.53 1.06-.53s1.06 0 1.77.18c.35 0 .53.35.71.53.35.35.53.89.71 1.42s.53.53 1.06.53c1.06-.35 2.12-.53 3.01-.89.53-.18.71-.53.53-1.06 0-.53-.18-.89-.18-1.42 0-.35.18-.71.35-.89.35-.53.89-.71 1.42-1.06.35-.18.71-.18.89-.18.53 0 .89.35 1.42.53.35.18.89.18 1.06-.18.53-1.06 1.24-2.12 1.77-3.19.18-.35 0-.71-.35-1.06s-.71-.53-1.24-.89c-.35-.18-.53-.53-.53-1.06s0-1.24.18-1.77c0-.18.35-.53.53-.71.53-.35.89-.53 1.42-.71.53-.18.71-.53.53-1.06-.35-1.06-.53-2.12-.89-3.19-.18-.53-.53-.71-1.06-.53-.53 0-1.06.18-1.59.18-.35 0-.53-.18-.89-.35-.35-.35-.71-.89-1.06-1.42-.18-.35-.18-.71-.18-.89-.38-.51-.02-1.04.15-1.39zm-2.65 12.21c-1.42 3.19-4.96 4.6-8.14 3.19-3.19-1.42-4.6-5.13-3.01-8.14 1.42-3.01 5.13-4.6 8.32-3.01 2.65 1.24 4.24 4.6 2.83 7.96z" />
                  </svg>
                </ButtonRounded>
                <ButtonRounded
                  v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
                  @action="viewPlanning(index)"
                  title="Ver Muestra"
                  color="blue"
                  large
                >
                  <svg class="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 283.5 283.5">
                    <path d="M275.2 50.3c-3.5-4.5-7.7-8.3-12.7-11.2-7.3-4.4-15.1-7.5-23.7-8.3-5.1-.5-10.2-.5-15.2.8-4.8 1.3-9 3.5-12.6 7-3.3 3.2-4.8 7.1-5.4 11.6-.1.9-.2 1.9-.4 2.8-.2 1.3-.5 1.6-1.9 1.7-2 .1-4 .1-6.1-.1-4.7-.3-9.3-.8-14-.7-1.6 0-3.2.1-4.7-.1-4.7-.5-9.3-.4-14-.6-5.8-.2-11.6-.1-17.4-.1-7.4 0-14.8-.1-22.1.2-5.7.2-11.5.7-17.2.5-.9 0-1.8.1-2.6.1-4.1.3-8.2.7-12.3.6-.9 0-1.9 0-2.8.1-4.5.4-8.9.7-13.4.7-.8 0-1.5 0-2.3.1-4.9.6-9.9.7-14.9 1.3-4 .5-8 1-11.9 1.9-5.5 1.2-10.8 3-15.9 5.3-9.1 4.1-16.1 10.5-20.9 19.3-4.7 8.8-6.4 18.4-6.5 28.3 0 1.4.3 2.9.3 4.3 0 2.7.5 5.4 1 8.1 1.3 6.6 3.5 13 6.5 19.1 3 6.1 6.5 11.8 10.7 17.1 3.6 4.5 7.5 8.8 11.9 12.7 7.2 6.4 15.1 11.6 23.9 15.4 6 2.6 12.2 4.8 18.6 6.2 3 .6 6 1.3 9.1 1.7 4.7.7 9.5.9 14.3 1.2 4.7.3 9.3-.1 14-.5 4.6-.3 9.3-1.1 13.8-2 5.7-1.1 11.2-2.6 16.6-4.6 10.8-4.1 20.5-10.1 28.3-18.7 4.2-4.6 7.4-9.8 8.7-16.1 1.2-5.9.5-11.5-2.1-16.9-1.9-4-4.5-7.6-7.9-10.6-2.8-2.4-5.9-4.3-9.3-5.7-7.8-3.2-16-3.4-24.2-2.8-5.9.4-11.7 2-17.2 4.1-9.1 3.5-17.3 8.6-23.9 15.9-3.1 3.5-5.6 7.3-7.2 11.7-.8 2.1-1.3 4.3-1.6 6.6-.2 1.8-1.2 2.8-2.9 3.3-2.6.7-5.1 1.3-7.8 1.6-6.5.8-12.6-.5-18.4-3.1-6-2.6-11-6.5-14.8-11.7-4.6-6.2-7.3-13.1-7.3-20.9v-4.2c0-6 1.8-11.4 4.9-16.5 4.3-6.8 10.6-11.1 17.8-14.2 5.2-2.3 10.7-3.7 16.3-4.8 5-1 10-1.5 15-2.1 2.6-.3 5.3-.3 7.9-.3 5.4.1 10.7-.1 16.1.3 5 .3 10.1-.1 15.1-.2 7.7-.1 15.4-.4 23.1-.7 8.3-.3 16.7-.2 25 .4 2.3.2 4.7.1 7 .4 5.1.6 10.1 1.2 15.1 2.5 4.3 1.1 8.6 2.4 12.6 4.6 5.3 2.9 9.1 7.1 11.4 12.7 2 4.9 3.1 10.1 3.8 15.4.7 5.4 1 10.8 1 16.2 0 4-.5 7.9-.4 11.9 0 .3 0 .5-.1.8-.7 5.2-1 10.5-2 15.7-.6 2.9-1.2 5.8-2.1 8.6-1.5 4.8-3.5 9.3-6 13.7-2.3 4-5.1 7.7-8.2 11.1-7.2 7.7-16.1 12.9-25.9 16.5-2.8 1.1-5.7 1.9-8.7 2.7-3.9 1.1-7.8 1.9-11.8 2.7-4 .7-8.1 1.2-12.1 1.8-4.5.6-9.1.7-13.6.9-7.1.3-14.2-.2-21.3-.9-4.5-.5-8.9-1.1-13.3-1.9-6.6-1.2-13-2.9-19.4-5-9-2.9-17.6-6.6-25.9-11-5.9-3.2-11.6-6.8-17.1-10.8-4.7-3.5-9.8-6.5-15.5-8-4-1.1-7.3 0-10.6 2.1-.9.6-1.8 1.4-2.5 2.3-2.8 3.6-2.8 7.6-2 11.7 1 5.1 3.5 9.6 6.6 13.7 3.3 4.3 7.5 7.5 11.8 10.5 5.3 3.7 10.9 6.9 16.7 9.6 6.5 3 13.2 5.7 20.1 7.9 6.6 2.1 13.2 3.7 19.9 5.2 2 .4 3.9.8 5.9 1.2 2.7.4 5.4.4 8 1.3.5.2 1 .2 1.5.2 3.2.1 6.3.6 9.4 1 6.2.7 12.3.7 18.5.9 4.1.1 8.2.1 12.3-.3 3.2-.3 6.4.1 9.6-.5 2.3-.4 4.7-.1 7-.6 2.9-.6 6-.2 8.7-1.5.2-.1.4-.1.6-.1 2.7-.2 5.4-.6 8-1.2 7-1.6 14-3.3 20.8-5.5 8.6-2.8 16.8-6.6 24.6-11.1 8.1-4.6 15.4-10.3 21.7-17.2 2.7-3 5.3-6.1 7.5-9.5 3.4-5.2 6-10.9 7.8-17 2.3-8.2 3.5-16.5 3.9-25 .5-10.8-.2-21.6-1.6-32.3-.6-4.1-1-8.3-2-12.3l-.9-4.5c-.8-4.2-1.7-8.4-2.8-12.5-.7-2.6-1.5-5.2-2.2-7.8-.4-1.5 0-2 1.4-2.3 1-.3 2.1-.5 3.1-.8 7.4-1.9 13.6-5.8 18.4-11.9 2.3-2.9 4.1-6.2 4.7-9.8 1.4-6.9-.5-13.2-4.8-18.8zM254.8 56c-1.7 6.6-10.1 7-14.7 4 0 0-2.5-10.2.7-12.8 3.2-2.6 7.4-1.1 7.4-1.1s8.2 3.3 6.6 9.9z" />
                  </svg>
                </ButtonRounded>
                <button
                  v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
                  class="flex justify-center items-center bg-white text-verde border border-verde-oscuro w-10 h-10 rounded-full focus:outline-none"
                  :class="{'hover:bg-verde': !item.sampleDelivered}"
                  title="Marcar Muestra recibida"
                  @click.prevent="checkSampleReceived(index, item.idSample)"
                >
                  <svg v-if="item.sampleDelivered" class="w-6 h-6 fill-current" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
                <ButtonRounded
                  v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
                  @action="openDiscontinueModal(index, item.idSample)"
                  title="Descatalogar muestra"
                  color="red"
                  large
                >
                  <svg class="w-6 h-6 fill-current" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      d="M6 18L18 6M6 6L18 18"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </ButtonRounded>
              </div>
            </div>
          </li>
            </draggable>
        </ul>

        <PlanningPagination
          :current-page="page"
          :total-rows="totalRows"
          :visible-rows="visibleRows"
          @next-page="fetchPaginationData('next');"
          @previous-page="fetchPaginationData('previous');"
          @load-selected-page="fetchPaginationData"
          @take-rows="takeRows"
        />
      </div>
    </section>

    <!-- === CALENDAR AND TOTAL PLANNINGS === -->
    <section class="w-full my-8 flex flex-wrap justify-between items-start">
      <!-- CALENDAR  -->
      <div class="w-64 mx-auto sm:m-0 space-y-4">
        <v-calendar
          is-inline
          @dayclick="goToCalendar"
          :attributes="attrs"
          mode="multiple"
          title-position="left"
          @update:from-page="fetchCalendarTasks($event)"
          v-if="hasRoles(['ADMIN', 'DIRECCIÓN', 'EQUIPO EUROPROSEM'])"
        />
        <ButtonRedirect route="calendar" v-if="hasRoles(['ADMIN', 'DIRECCIÓN', 'EQUIPO EUROPROSEM'])">
          {{ $t("lang.msg.openCalendar") }}
        </ButtonRedirect>
      </div>
      <!-- TOTAL PLANNINGS  -->
      <div class="w-64 mx-auto mt-8 sm:m-0 text-center space-y-4">
        <div class="bg-white text-primario py-4 rounded-sm">
          <span>Total visualizado</span>
          <span class="text-4xl ml-2">{{ totalPlannings }}</span>
        </div>
        <ButtonAction @action="printPlanningOnPdf" large>
          {{ $t("lang.msg.printList") }}
        </ButtonAction>
      </div>
    </section>

    <TheModal :open="isModalOpen" title="Descatalogar Muestra" @close="isModalOpen = false">
      <template #body>
        <p class="mt-4 pr-16 text-gray-700 font-semibold">
          {{ $t('lang.msg.discontinueSample') }}
        </p>
        <textarea
          rows="2"
          class="form-textarea w-full mt-2"
          placeholder="Escriba el motivo aquí"
          maxlength="256"
          v-model="current.cancellationReason"
        />
          <span>{{current.cancellationReason? current.cancellationReason.length : 0}}/256</span>
      </template>
      <template #action>
        <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
          <button
            type="button"
            class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-red-600 hover:bg-red-700 text-base font-medium text-white shadow-sm focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150"
            @click="discontinuePlanning"
          >{{ $t('lang.msg.discontinue') }}</button>
        </span>
      </template>
    </TheModal>
      <TheModal
          :open="isNextStepModalOpen"
          :title="$t('lang.msg.nextStep')"
          @close="isNextStepModalOpen=false"
      >
          <template #body>
              <Confirmation :to="'Producción'"/>
          </template>
          <template #action>
    <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
      <button
          type="button"
          class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 hover:bg-green-700 text-base font-medium text-white shadow-sm focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150"
          @click="planningNextStep(planningIndex)">Aceptar</button>
    </span>
          </template>
      </TheModal>
    <!-- ======================== -->
    <!-- === SLIDE OVER PANEL === -->
    <!-- ======================== -->
    <SlideOver :open="isSlideOpen" @close="closeSlide">
      <Notifications
        :extra-params="extraParams"
        :notifications="planningNotifications"
        @note-added="noteAdded"
        @client-changed="notificationsClientChanged"
      />
    </SlideOver>
  </div>
</template>

<script>
// jsPDF
import jsPDF from "jspdf";
import "jspdf-autotable";

// MIXINS
import PlanningMixin from "@/components/mixins/PlanningMixin";
import TasksMixin from "@/components/mixins/TasksMixin";

// COMPONENTS
import TheModal from "@/components/shared/TheModal";
import SlideOver from "@/components/shared/SlideOver";
import Notifications from "@/components/Notifications";
import PlanningDays from "@/components/shared/planning/PlanningDays";
import PlanningTableItem from "@/components/shared/planning/PlanningTableItem";
import PlanningPagination from "@/components/shared/planning/PlanningPagination";
import draggable from 'vuedraggable'
import Confirmation from "@/components/modals/Confirmation";

export default {
  name: "PlanningSample",
  components: {
    TheModal,
    SlideOver,
    Notifications,
    PlanningDays,
    PlanningTableItem,
    PlanningPagination,
      Confirmation,
      draggable
  },
  mixins: [PlanningMixin, TasksMixin],
  data() {
    return {
      homeFilter: 0,
      isModalOpen: false,
      // SLIDE OVER
      isSlideOpen: false,
      planningNotifications: {},
      extraParams: {
        sample: {}
      },
      showClientNotifications: true,
        isNextStepModalOpen: false,
        planningIndex:0
    };
  },
  created() {
    // Change the planning title based on 'fromHome' route param
    if (this.$route.params.fromHome) {
      this.title = this.$t("lang.msg.samplePlanningPendingProduction");
    } else {
      this.title = this.$t("lang.msg.samplePlannings");
    }

    // If the user comes from the 'Home' component.
    this.homeFilter = this.$route.params.homeFilter;

    // Pagination and Total records for different filters.
    if (this.homeFilter == 1) {
      this.title = this.$t("lang.msg.samplesWithoutFactory");
      this.setPaginationUrls(
        "api/Samples/PaginationSinFabrica",
        "api/Samples/TotalRegistrosSinFabrica"
      );
    } else if (this.homeFilter == 2) {
      this.title = this.$t("lang.msg.receivedSamples");
      this.setPaginationUrls(
        "api/Samples/PaginationEntregada",
        "api/Samples/TotalRegistrosEntregadas"
      );
    } else if (this.homeFilter == 3) {
      this.title = this.$t("lang.msg.noReceivedSamples");
      this.setPaginationUrls(
        "api/Samples/PaginationNoRecibida",
        "api/Samples/TotalRegistrosNoRecibida"
      );
    } else if (this.homeFilter == 4){
        this.title = this.$t("lang.msg.discontinuedSamples");
        this.setPaginationUrls(
            "api/Samples/PaginationDescatalogada",
            "api/Samples/TotalRegistrosDescatalogada"
        );
    }else {
      this.setPaginationUrls(
        "api/Samples/PaginationNoProduction",
        "api/Samples/TotalRegistrosNoProduction"
      );
    }

    this.fetchPaginationData();
    this.fetchCalendarTasks();
  },
  mounted() {
    // If the user comes from the 'Notification' bell.
    if (this.$route.params.idSample) {
      this.$axios
        .post(`api/Samples/GetSample`, {
          idSample: parseInt(this.$route.params.idSample)
        })
        .then((response) => {
          this.openNotificationsModal(response.data[0]);
        });
    }
  },
    computed:{
        isSmallScreen(){
            return window.innerWidth < 1280
        }
    },
  methods: {
      changeFilter(v){
          this.isFiltering=v!==undefined
      },
      confirmStepToProduction(i) {
          this.planningIndex = i
          this.isNextStepModalOpen = true;
      },
      filterAction(){
          if (this.homeFilter == 4) {
              this.filterPlannings('api/Samples/DiscontinuedGetBy', 'api/Samples/DiscontinuedTotalRegistrosGetBy')
          }else{
              this.filterPlannings('api/Samples/GetBy', 'api/Samples/TotalRegistrosGetBy')
          }
      },
    planningNextStep(index) {
      this.$axios
        .post("api/Productions/Add", {
          idUser: this.user.id,
          idSample: this.planning[index].idSample,
          idLicense: null,
          idPaymentMethod: null,
          productionDelivered: false,
          productionDeliveredDate: "2020-01-01T00:00:00.000Z",
          productionDeliveredUser: null,
          productionDeliveryDate: "2020-01-01T00:00:00.000Z",
          productionOriginal: true,
          productionQuantity: null,
          productionReoperation: false,
          productionTaskProductionEndDate: "2020-01-01T00:00:00.000Z"
        })
        .then((response) => {
          let idProduction = 0;
          if (typeof response.data === "number") {
            idProduction = response.data;
          } else {
            idProduction = response.data.idProduction;
          }

          // Push the route to 'Production' with 'idProduction'
          this.$router.push({
            name: "production",
            params: {
              idProduction: idProduction
            }
          });
        })
        .catch(() => this.showFriendlyError());
    },
    viewPlanning(index) {
      if (this.hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])) {
        this.$router.push({
          name: "sample",
          params: {
            idSample: this.planning[index].idSample
          }
        });
      }
    },
    openDiscontinueModal(index, idSample) {
      this.isModalOpen = true;
      this.current.index = index;
      this.current.idSample = idSample;
    },
    discontinuePlanning() {
      this.$axios
        .post("api/Samples/Descatalogo", {
          idUser: this.user.id,
          idSample: this.current.idSample,
          sampleCancellationReason: this.current.cancellationReason
        })
        .then(() => {
          this.planning.splice(this.current.index, 1);
          this.current = {};
          this.isModalOpen = false;
          this.openAlert("success", "Muestra descatalogada", null);
        })
        .catch(() => this.showFriendlyError());
    },
    checkSampleReceived(index, idSample) {
      let isChecked = this.planning[index].sampleDelivered;
      let message = "";

      if (isChecked) message = "Ha desmarcado la muestra como recibida";
      else message = "Muestra recibida";
      this.$axios
        .post("api/Samples/Entregada", {
          idUser: this.user.id,
          idSample: idSample,
          sampleDelivered: !isChecked
        })
        .then(() => {
          this.planning[index].sampleDelivered = !isChecked;
          this.openAlert("success", message, null);
        })
        .catch(() => this.showFriendlyError());
    },
    // === SLIDE OVER FUNCTIONS. ===
    openNotificationsModal(sample) {
      this.showClientNotifications = this.hasRoles(['FABRICA']) ? false : true;

      this.initExtraParams(sample);

      this.fetchSampleComments(sample, this.showClientNotifications);
    },
    initExtraParams(sample) {
      // Object with extra parameters that will be used on 'Notifications' component.
      if (sample != null) {
        this.extraParams = {
          sample: sample,
          referencia:
            sample.comercialRequestReference + sample.comercialRequestReferenceNumber,
          idFactory: sample.idFactory,
          cliente: this.showClientNotifications
        };
      } else {
        this.extraParams = {
          sample: {},
          referencia: "",
          idFactory: 0,
          cliente: this.showClientNotifications
        };
      }
    },
    fetchSampleComments(sample, showClientNotifications) {
      this.$axios
        .post(`api/Notificaciones/GetComentario`, {
          rol: this.user.rol,
          idSample: sample.idSample,
          idUser: this.user.id,
          cliente: showClientNotifications
        })
        .then((response) => {
          this.currentSample = sample;
          this.planningNotifications = response.data;
          this.isSlideOpen = true;
        });
    },
    notificationsClientChanged({ sample, cliente }) {
      // Client is undefined after closing the SlideOver
      if (cliente !== undefined) {
        this.fetchSampleComments(sample, cliente);
      }
    },
    closeSlide() {
      this.showClientNotifications = true;
      this.isSlideOpen = false;

      this.initExtraParams(null);
    },
    noteAdded(item) {
      this.planningNotifications.unshift(item);
    },
    // PRINT PLANNING ON PDF
    printPlanningOnPdf() {
      // Only pt supported (not mm or in)
      var doc = new jsPDF("l", "pt");

        const head = [['','Referencia interna','Diseñador interno','Diseñador cliente',
            'Comercial interno','Comercial cliente','Cliente','Dpto.','Color','Artículo','Composición',
        'Fábrica','Origen','Colección','Fecha envío a fábrica','Fecha estimada de llegada']]
        const body = [[]];
        let planningClone = [...this.planning ];
        planningClone.sort((a,b)=>a.clientName.localeCompare(b.clientName));
        doc.text("Planning Muestras", 40, 30);
        planningClone.forEach((element,index) => {
            body.push([]);
            body[index].push(index+1);
            body[index].push(element.comercialRequestReference);
            body[index].push(element.designerName);
            body[index].push(element.clientDesignerName);
            body[index].push(element.employeeName);
            body[index].push(element.buyerName);
            body[index].push(element.clientName);
            body[index].push(element.sectionName);
            body[index].push(element.baseColorEs);
            body[index].push(element.subfamilyArticleEs);
            body[index].push(element.compositionEs);
            body[index].push(element.factoryName);
            body[index].push(element.factoryLocation);
            body[index].push(element.collectionEs);
            body[index].push(element.fechaEnvioFabrica);
            body[index].push(element.fechaEstimadaLlegada);
        })
        doc.autoTable({
            head:head,
            body:body,
            foot:[['Total: '+planningClone.length]],
            styles: { fontSize: 6 },
            theme: "striped",
            margin: { top: 40 }
        });


      let currentDate = this.$moment().format("DDMMYYYY HHmmss");

      // Print task in PDF.
      doc.save(`PlanningMuestra_${currentDate}.pdf`);
    }
  }
};
</script>
<style lang="css">
.border-red{
    border: 2px solid #ffc4c4;
}
</style>
